import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Globe, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { COMPANY, PROJECT_TYPES, REGIONS } from "@/lib/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  business_email: "",
  company_name: "",
  country: "",
  project_type: "",
  project_description: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // basic validation
    for (const [k, v] of Object.entries(form)) {
      if (!String(v).trim()) {
        toast.error("Please complete all fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Our team will be in touch.");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const allCountries = Array.from(
    new Set(REGIONS.flatMap((r) => r.countries))
  ).sort();

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg opacity-70 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24">
          <p className="vd-eyebrow">Contact</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-slate-900 max-w-4xl">
            Let’s discuss your{" "}
            <span className="text-teal-700">next research project</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed">
            Share a few details about your project and our team
            will follow up within one business day.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left column - info */}
          <aside className="lg:col-span-4 space-y-8">
            <div className="border border-slate-200 rounded-md p-6 bg-white">
              <p className="vd-eyebrow">Get in touch</p>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email-link"
                className="mt-4 flex items-center gap-3 text-slate-900 hover:text-teal-700 transition-colors"
              >
                <Mail className="h-5 w-5 text-teal-700" />
                <span className="font-medium">{COMPANY.email}</span>
              </a>
              <div className="mt-3 flex items-center gap-3 text-slate-600">
                <Globe className="h-5 w-5 text-teal-700" />
                <span>{COMPANY.website}</span>
              </div>
              <div className="mt-3 flex items-center gap-3 text-slate-600">
                <Clock className="h-5 w-5 text-teal-700" />
                <span>Response within 1 business day</span>
              </div>
            </div>

            <div className="border border-slate-200 rounded-md p-6 bg-white">
              <p className="vd-eyebrow">Global Coverage</p>
              <div className="mt-4 space-y-4">
                {REGIONS.map((r) => (
                  <div key={r.region}>
                    <p className="text-sm font-semibold text-slate-900">
                      {r.region}
                    </p>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      {r.countries.join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-slate-200 rounded-md p-6 bg-[#0b192c] text-white">
              <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
                Trusted · Timely · Thorough
              </p>
              <p className="mt-4 font-display text-lg leading-snug text-white">
                One partner. Full research lifecycle. Global execution.
              </p>
            </div>
          </aside>

          {/* Right column - form */}
          <div className="lg:col-span-8">
            <div className="border border-slate-200 rounded-md bg-white p-8 md:p-10">
              {submitted ? (
                <div
                  data-testid="contact-success-message"
                  className="py-10 text-center"
                >
                  <div className="mx-auto h-14 w-14 rounded-full border border-teal-600 flex items-center justify-center">
                    <CheckCircle2 className="h-7 w-7 text-teal-700" />
                  </div>
                  <h2 className="mt-6 font-display text-2xl md:text-3xl font-semibold text-slate-900">
                    Inquiry received
                  </h2>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">
                    Thank you. A member of our team will reach out
                    within one business day to discuss your project.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-submit-another"
                    className="mt-8 inline-flex items-center gap-2 text-slate-900 font-medium border-b border-slate-900 pb-1 hover:border-orange-600 hover:text-orange-700 transition-colors"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-6"
                >
                  <p className="vd-eyebrow">Project Inquiry</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field
                      label="Full Name"
                      testId="contact-input-name"
                      required
                      value={form.full_name}
                      onChange={update("full_name")}
                      placeholder="Jane Doe"
                    />
                    <Field
                      label="Business Email"
                      type="email"
                      testId="contact-input-email"
                      required
                      value={form.business_email}
                      onChange={update("business_email")}
                      placeholder="jane@company.com"
                    />
                    <Field
                      label="Company Name"
                      testId="contact-input-company"
                      required
                      value={form.company_name}
                      onChange={update("company_name")}
                      placeholder="Acme Insights"
                    />
                    <SelectField
                      label="Country"
                      testId="contact-input-country"
                      required
                      value={form.country}
                      onChange={update("country")}
                      options={allCountries}
                      placeholder="Select country"
                    />
                  </div>
                  <SelectField
                    label="Project Type"
                    testId="contact-input-project-type"
                    required
                    value={form.project_type}
                    onChange={update("project_type")}
                    options={PROJECT_TYPES}
                    placeholder="Select project type"
                  />
                  <TextArea
                    label="Project Description"
                    testId="contact-input-description"
                    required
                    value={form.project_description}
                    onChange={update("project_description")}
                    placeholder="Tell us about your audience, markets, methodology, and timeline."
                  />

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium px-6 py-3.5 rounded-md transition-colors shadow-sm"
                  >
                    {submitting ? "Submitting..." : "Request a Consultation"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-slate-500">
                    By submitting, you agree to be contacted by VeriData Global
                    regarding your inquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, value, onChange, placeholder, type = "text", required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-700">
        {label}
        {required && <span className="text-orange-600 ml-1">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        required={required}
        className="mt-2 w-full border border-slate-300 rounded-md px-4 py-3 text-[15px] text-slate-900 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-shadow"
      />
    </label>
  );
}

function SelectField({ label, testId, value, onChange, options, placeholder, required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-700">
        {label}
        {required && <span className="text-orange-600 ml-1">*</span>}
      </span>
      <select
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="mt-2 w-full border border-slate-300 rounded-md px-4 py-3 text-[15px] text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-shadow"
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextArea({ label, testId, value, onChange, placeholder, required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-700">
        {label}
        {required && <span className="text-orange-600 ml-1">*</span>}
      </span>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        rows={5}
        data-testid={testId}
        className="mt-2 w-full border border-slate-300 rounded-md px-4 py-3 text-[15px] text-slate-900 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-teal-600 transition-shadow resize-y"
      />
    </label>
  );
}
